import {
  createFrontendModule,
  createFrontendPlugin,
  PageBlueprint,
  PluginHeaderActionBlueprint,
} from '@backstage/frontend-plugin-api';
import {
  EntityCardBlueprint,
  EntityContentBlueprint,
} from '@backstage/plugin-catalog-react/alpha';
import techdocsPlugin from '@backstage/plugin-techdocs/alpha';
import { TechDocsAddonLocations } from '@backstage/plugin-techdocs-react';
import { AddonBlueprint } from '@backstage/plugin-techdocs-react/alpha';
import { techdocsAddonsApiRef } from './addons/techdocsAddons';
import { hasDocsAnnotation } from './entity-content/hasDocsAnnotation';
import { DocsReaderStyles } from './reader/docsReaderStyles';
import { InlineSwaggerUi } from './reader/inlineSwaggerUi';
import { DocsIcon } from '@xlrt/components';
import { rootDocsRouteRef } from './routes';

/**
 * The documentation hub at `/docs`. It replaces the core TechDocs index
 * (`page:techdocs`, disabled in `app-config.yaml`) and mounts its route ref,
 * so links to the TechDocs root, like the "Docs" breadcrumb of the TechDocs
 * reader, open the hub.
 */
export const documentationHubPage = PageBlueprint.make({
  params: {
    path: '/docs',
    title: 'Docs',
    icon: <DocsIcon />,
    routeRef: techdocsPlugin.routes.root,
    loader: () =>
      import('./components/DocumentationHub').then(m => <m.DocumentationHub />),
  },
});

/**
 * Static HTML documentation of an entity, served through the staticdocs
 * backend. A page of the TechDocs plugin (see `techdocsPagesModule`), so its
 * "Docs" breadcrumb opens the hub like the one of the TechDocs reader.
 */
export const staticDocsPage = PageBlueprint.make({
  name: 'static-docs',
  params: {
    path: '/static-docs/:namespace/:kind/:name',
    title: 'Docs',
    icon: <DocsIcon />,
    loader: () =>
      import('./static-docs/StaticDocsReaderPage').then(m => (
        <m.StaticDocsReaderPage />
      )),
  },
});

/**
 * TechDocs addons. They attach to the TechDocs addons API, which feeds both
 * the reader page and the docs entity tab.
 */
export const inlineSwaggerUiAddon = AddonBlueprint.make({
  name: 'inline-swagger-ui',
  params: {
    name: 'InlineSwaggerUI',
    location: TechDocsAddonLocations.Content,
    component: InlineSwaggerUi,
  },
});

export const readerStylesAddon = AddonBlueprint.make({
  name: 'reader-styles',
  params: {
    name: 'ReaderStylesExtension',
    location: TechDocsAddonLocations.Content,
    component: DocsReaderStyles,
  },
});

/**
 * The Docs tab of the entity page, replacing the core `entity-content:techdocs`
 * (disabled in `app-config.yaml`): static docs or TechDocs, behind the
 * limited-access permission. Shown for entities with a docs annotation.
 */
export const docsEntityContent = EntityContentBlueprint.make({
  name: 'docs',
  params: {
    path: 'docs',
    title: 'Docs',
    group: 'documentation',
    filter: hasDocsAnnotation,
    loader: () => import('./entity-content/DocsTab').then(m => <m.DocsTab />),
  },
});

/** TechDocs upload credentials of a user entity (issuer and subject). */
export const userApiAuthCard = EntityCardBlueprint.make({
  name: 'user-api-auth-card',
  params: {
    filter: { kind: 'user' },
    loader: () => import('./upload/ApiAuthCard').then(m => <m.ApiAuthCard />),
  },
});

/** TechDocs upload key and its expiry of a group entity. */
export const groupApiAuthCard = EntityCardBlueprint.make({
  name: 'group-api-auth-card',
  params: {
    filter: { kind: 'group' },
    loader: () =>
      import('./upload/ApiAuthCardGroup').then(m => <m.ApiAuthCardGroup />),
  },
});

export const documentationHubFrontendPlugin = createFrontendPlugin({
  pluginId: 'documentation-hub',
  extensions: [
    documentationHubPage,
    inlineSwaggerUiAddon,
    readerStylesAddon,
    docsEntityContent,
    userApiAuthCard,
    groupApiAuthCard,
  ],
  externalRoutes: {
    docRoot: rootDocsRouteRef,
  },
});

/**
 * The metadata and actions of the document open in a docs reader (owner,
 * entity, last update, copy link, star, full screen) in the page header.
 */
export const docsReaderHeaderAction = PluginHeaderActionBlueprint.make({
  name: 'docs-reader',
  params: {
    loader: () =>
      import('./components/DocsHeader').then(m => (
        <m.DocsReaderHeaderActions />
      )),
  },
});

/**
 * The docs reader pages, as pages of the core TechDocs plugin: the DXP reader
 * replaces the core reader page (`page:techdocs/reader`) and keeps its path
 * and route ref, so `techdocsPlugin.routes.docRoot` still resolves to it; the
 * static docs page; the header action for both. Their title links to the
 * TechDocs root, which the documentation hub mounts. Install next to
 * `documentationHubFrontendPlugin`.
 */
export const techdocsPagesModule = createFrontendModule({
  pluginId: 'techdocs',
  extensions: [
    staticDocsPage,
    docsReaderHeaderAction,
    techdocsPlugin.getExtension('page:techdocs/reader').override({
      factory: (originalFactory, { apis, inputs }) =>
        originalFactory({
          params: {
            title: 'Docs',
            icon: <DocsIcon />,
            loader: async () => {
              // Addons registered through the addons API (every
              // `AddonBlueprint`), plus any attached to the page directly.
              const apiAddons =
                apis.get(techdocsAddonsApiRef)?.getAddons() ?? [];
              const directAddons = inputs.addons.map(addon =>
                addon.get(AddonBlueprint.dataRefs.addon),
              );
              const { DxpReaderPage } = await import('./reader/dxpReaderPage');
              return <DxpReaderPage addons={[...apiAddons, ...directAddons]} />;
            },
          },
        }),
    }),
  ],
});
